import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import dishesData from '../data/dishesData';

const GalleryContainer = styled(motion.div)`
  width: 100%;
  min-width: 320px;
  padding: 4rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  background: linear-gradient(to bottom,rgb(17, 3, 40) 20%, #6C2FF2 50%,#0A0118 100%);
  min-height: 100vh;
  overflow: hidden;
`;

const GalleryTitle = styled(motion.h2)`
  font-size: 32px;
  position: relative;
  top: 100px;
//   margin-bottom: 4rem;
  color: white;
  text-align: center;
  text-transform: uppercase;
  font-family: 'Black Han Sans', sans-serif;
  font-weight: 700;
  background: none;
  -webkit-background-clip: initial;
  -webkit-text-fill-color: white;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
`;

const DishesContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 280px));
  gap: 25px;
  width: 100%;
  max-width: 1200px;
  margin: 120px auto 0;
  padding: 0 2rem;
  justify-content: center;
`;

const DishCard = styled(motion.div)`
  position: relative;
  aspect-ratio: 1 / 1.2;
  min-width: 260px;
  min-height: 312px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 
    0 10px 15px -3px rgba(0, 0, 0, 0.4),
    0 4px 6px -2px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(255, 255, 255, 0.05),
    inset 0 1px 1px rgba(255, 255, 255, 0.1);
  background: rgba(25, 25, 25, 0.95);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  cursor: pointer;
  transform: translateY(0);
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 
      0 20px 25px -5px rgba(0, 0, 0, 0.4),
      0 10px 10px -5px rgba(0, 0, 0, 0.3),
      0 0 0 1px rgba(255, 255, 255, 0.05),
      inset 0 1px 1px rgba(255, 255, 255, 0.1),
      0 0 20px 5px rgba(108, 47, 242, 0.15);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 40%;
    background: linear-gradient(to bottom, 
      rgba(255, 255, 255, 0.08) 0%, 
      rgba(255, 255, 255, 0) 100%);
    border-radius: 20px 20px 0 0;
    opacity: 0.6;
    pointer-events: none;
  }
`;

const DishImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.5s ease;
  filter: brightness(1.05) contrast(1.1) saturate(1.15);
  
  ${DishCard}:hover & {
    transform: scale(1.07);
  }
`;

const DishOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60%;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.85) 0%,
    rgba(0, 0, 0, 0.65) 40%,
    rgba(0, 0, 0, 0.2) 80%,
    rgba(0, 0, 0, 0) 100%
  );
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 1.5rem;
  border-radius: 0 0 20px 20px;
`;

const DishName = styled.h3`
  font-size: 1.35rem;
  color: white;
  margin: 0 0 0.5rem 0;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.7);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    height: 2px;
    width: 40px;
    background: linear-gradient(to right, #6C2FF2, #9162FF);
    border-radius: 2px;
    transition: width 0.3s ease;
  }
  
  ${DishCard}:hover &::after {
    width: 60px;
  }
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1rem;
  background: rgba(10, 10, 10, 0.5);
  backdrop-filter: blur(4px);
  padding: 0.4rem 0.7rem;
  border-radius: 20px;
  width: fit-content;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
`;

const Star = styled.span`
  color: #FFD700;
  font-size: 1.2rem;
  margin-right: 2px;
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.4));
`;

const RatingValue = styled.span`
  font-size: 1.1rem;
  color: white;
  font-weight: 600;
  margin-left: 8px;
  letter-spacing: 0.5px;
`;

// Анимация для контейнера
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1
    }
  }
};

// Анимация для карточек
const cardVariants = {
  hidden: { y: 50, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      damping: 20,
      stiffness: 100,
      duration: 0.4
    }
  }
};

const DishesGallery = () => {
  // Ограничиваем до 8 блюд для лучшей демонстрации сетки
  const limitedDishes = dishesData.slice(0, 8);
  
  // Ссылка на App Store
  const appStoreUrl = "https://apps.apple.com/us/app/chef-kats-ai/id6739225693";
  
  // Функция для перехода на App Store
  const navigateToAppStore = () => {
    window.open(appStoreUrl, '_blank');
  };
  
  return (
    <GalleryContainer
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.05 }}
      variants={containerVariants}
    >
      <GalleryTitle
        initial={{ opacity: 0, y: -30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        viewport={{ once: true }}
      >
         Awesome dishes just for you
      </GalleryTitle>
      
      <DishesContainer>
        {limitedDishes.map((dish) => (
          <DishCard 
            key={dish.id}
            variants={cardVariants}
            onClick={navigateToAppStore}
          >
            <DishImage 
              src={dish.image} 
              alt={dish.name} 
            />
            <DishOverlay>
              <DishName>{dish.name}</DishName>
              <Rating>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i}>★</Star>
                ))}
                <RatingValue>5.0</RatingValue>
              </Rating>
            </DishOverlay>
          </DishCard>
        ))}
      </DishesContainer>
    </GalleryContainer>
  );
};

export default DishesGallery; 